import React, { useEffect, useMemo, useState } from "react";
import { CanEdit, useIsReadOnlyManager } from "@/shared/readOnlyManager";
import {
  useExpanded,
  usePagination,
  useResizeColumns,
  useSortBy,
  useTable,
  useRowSelect,
} from "react-table";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Table,
} from "reactstrap";
import {
  flashError,
  flashSuccess,
  useDispatch,
} from "@/shared/components/flash";
import axios from "axios";
import ReactTablePagination from "@/shared/components/table/components/ReactTablePagination";
import { TABLE_COLUMNS } from "./Column";
import { connect } from "react-redux";
import apiTableService from "@/services/apiTableService";
import IndeterminateCheckbox from "@/shared/components/form/IndeterminateCheckbox";
import AwardScheduleDateModel from "./AwardScheduleDateModel";
import DatePicker from "react-datepicker";
import AwardApprovalPopup from "./AwardApprovalPopup";
import { dateStrToYmd } from "@/shared/helpers";
import {
  TableSkeleton,
  ButtonSkeleton,
} from "@/shared/components/Skeletons";

const queryPageSize = 10;

const ManageScheduleAwardApprovals = ({
  auth,
  organization,
  program,
  togglePan,
}) => {
  const [participants, setParticipants] = useState(null);
  const [filter, setFilter] = useState({ keyword: "" });
  const [loading, setLoading] = useState(true);
  const [statusName, setStatusName] = useState("");
  const [participantsScheduleDateData, setParticipantsScheduleData] =
    useState(null);
  const [isOpen, setOpen] = useState(false);
  const [awardApprovalParticipants, setAwardApprovalParticipants] = useState(
    []
  );
  const [modelName, setModelName] = useState("");
  const [editingDateId, setEditingDateId] = useState(null);
  const readOnly = useIsReadOnlyManager();

  const dispatch = useDispatch();

  const toggle = () => {
    setOpen((prevState) => !prevState);
  };

  const SELECTION_COLUMN = {
    id: "selection",
    Header: ({ getToggleAllPageRowsSelectedProps }) => (
      <div>
        <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
      </div>
    ),
    Cell: ({ row }) => (
      <div>
        <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
      </div>
    ),
  };

  const manage_columns = readOnly ? TABLE_COLUMNS : [SELECTION_COLUMN, ...TABLE_COLUMNS];

  const columns = React.useMemo(
    () =>
      manage_columns
    // First remove unwanted columns 
        .filter( 
          (col) => 
            col.accessor !== "approval_status" && 
            col.accessor !== "rejection_notes" 
        )
        .map((column) => {
        if (column.accessor === "scheduled_date" && !readOnly) {
          return {
            ...column,
            Cell: ({ value, row }) => {
              const id = row.original.id;
              const isEditing = editingDateId === id;
              const dateValue = value ? value.substring(0, 10) : '';

              const parsedDisplay = (() => {
                if (!value) return null;
                const s = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value + 'T00:00:00' : value.replace(' ', 'T');
                const d = new Date(s);
                return isNaN(d.getTime()) ? null : d;
              })();
              const displayDate = parsedDisplay ? parsedDisplay.toLocaleDateString("en-US", {}) : 'Set date';

              return (
                <div style={{ width: '180px', minWidth: '180px', height: '24px', overflow: 'visible' }}>
                  {isEditing ? (
                    <DatePicker
                      selected={dateValue ? new Date(dateValue + 'T00:00:00') : null}
                      open
                      dateFormat="MM/dd/yyyy"
                      className="form-control form-control-sm"
                      style={{ width: '180px', position: 'absolute', zIndex: 10 }}
                      onClickOutside={() => setEditingDateId(null)}
                      onChange={(date) => {
                        if (!date) { setEditingDateId(null); return; }
                        const newDate = dateStrToYmd(date);
                        if (newDate !== dateValue) {
                          setEditingDateId(null);
                          setParticipants(prev => ({
                            ...prev,
                            results: prev.results?.map(r => r.id === id ? { ...r, scheduled_date: newDate } : r)
                          }));
                          axios.put(
                            `/organization/${organization?.id}/program/${program?.id}/scheduled-award/update-date`,
                            { id, scheduled_date: newDate }
                          ).then(() => {
                            flashSuccess(dispatch, "Schedule date updated!");
                          }).catch(err => {
                            setParticipants(prev => ({
                              ...prev,
                              results: prev.results?.map(r => r.id === id ? { ...r, scheduled_date: dateValue } : r)
                            }));
                            flashError(dispatch, err.message);
                          });
                        } else {
                          setEditingDateId(null);
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === 'Escape') setEditingDateId(null); }}
                    />
                  ) : (
                    <div className="d-flex gap-1 align-items-center">
                      <span>{displayDate}</span>
                      <button
                        type="button"
                        className="manage-approval-edit-button"
                        onClick={() => setEditingDateId(id)}
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              );
            },
          };
        }
        return column;
      }),
    [editingDateId, organization, program, readOnly]
  );

  const handleEdit = (row) => {
    if (row) {
      setParticipantsScheduleData(row);
      toggle();
      setModelName("Award Schedule Date");
    }
  };

  const onSelectAction = (name) => {
    const rows = selectedFlatRows.map((d) => d.original);
    if (rows.length === 0) {
      alert("Select participants");
      return;
    }
    setAwardApprovalParticipants(rows);
    setStatusName(name);
    setModelName("Award Approval Popup");
    toggle();
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    selectedFlatRows,
    allColumns,
    getToggleHideAllColumnsProps,
    getToggleHiddenProps,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns: columns,
      data: useMemo(
        () => (participants ? participants?.results : []),
        [participants]
      ),
      initialState: {
        pageIndex: 0,
        pageSize: queryPageSize,
      },
      manualPagination: true, // Tell the usePagination
      pageCount: Math.ceil(participants?.count / queryPageSize),
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
      disableResizing: true,
      autoResetHiddenColumns: false,
      striped: true,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useRowSelect
  );

  const manualPageSize = [];

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    if (organization?.id && program?.id) {
      apiTableService
        .fetchData({
          url: `/organization/${organization.id}/program/${program.id}/manage-schedule-approval`,
          page: pageIndex,
          size: queryPageSize,
        })
        .then((items) => {
          if (mounted && items) {
            setParticipants(items);
            setLoading(false);
          }
        })
        .catch((err) => {
          console.log('Fetch error:', err);
        });
    } else {
      setLoading(false);
    }
    return () => (mounted = false);
  }, [organization, program, togglePan, pageIndex, queryPageSize, filter]);


  const onSubmit = (values) => {
    try {
      if (awardApprovalParticipants?.length > 0) {
        const endpoint = statusName === 'approve' ? 'approve' : 'reject';
        const requests = awardApprovalParticipants.map((participant) =>
          axios.put(
            `/organization/${organization?.id}/program/${program?.id}/scheduled-award/${endpoint}`,
            { id: participant.id, rejection_note: values.rejection_note }
          )
        );

        Promise.all(requests)
          .then(() => {
            flashSuccess(dispatch, "Award status updated successfully!");
            toggle();
            const rejectedIds = awardApprovalParticipants.map(p => p.id);
            setParticipants(prev => ({
              ...prev,
              results: prev.results?.filter(r => !rejectedIds.includes(r.id))
            }));
          })
          .catch((error) => {
            flashError(dispatch, error.message);
          });
      }
    } catch (error) {
      flashError(dispatch, error.message);
    }
  };

  if (loading) {
    return (
      <>
        <ButtonSkeleton variant="rect" width={100} height={40} />
        <TableSkeleton rows={3} columns={7} width={"100%"} height={20} />
      </>
    );
  }

  return (
    <div>
        <div className="users">
          <div className="mb-2" style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px' }}>
            <CanEdit>
              <button className="btn btn-sm btn-danger" onClick={() => onSelectAction('reject')}>Reject</button>
            </CanEdit>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <UncontrolledDropdown>
                <DropdownToggle caret className="btn btn-sm btn-outline-secondary" style={{ borderRadius: 4 }}>
                  Toggle Columns
                </DropdownToggle>
                <DropdownMenu>
                  {allColumns &&
                    allColumns.map((column) => (
                      <DropdownItem key={column.id} toggle={false}>
                        <input
                          type="checkbox"
                          {...column.getToggleHiddenProps()}
                          style={{ marginRight: "8px" }}
                        />
                        <label>{typeof column.Header === 'string' ? column.Header : ''}</label>
                      </DropdownItem>
                    ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </div>
          <div className="points-summary-table">
            <Table striped borderless size="md" className="table--stack-mobile" {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td
                            {...cell.getCellProps()}
                            data-label={typeof cell.column.Header === "string" ? cell.column.Header : ""}
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>

        <div>
          {rows.length > 0 && (
            <>
              <ReactTablePagination
                page={page}
                gotoPage={gotoPage}
                previousPage={previousPage}
                nextPage={nextPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageSize={pageSize}
                pageIndex={pageIndex}
                pageCount={pageCount}
                setPageSize={setPageSize}
                manualPageSize={manualPageSize}
                dataLength={participants ? participants.count : 0}
              />
            </>
          )}
        </div>
        {participantsScheduleDateData && (
          <AwardScheduleDateModel
            isOpen={isOpen}
            setOpen={setOpen}
            toggle={toggle}
            organization={organization}
            program={program}
            participantsScheduleDateData={participantsScheduleDateData}
            togglePan={togglePan}
            loading={loading}
          />
        )}
        {modelName === "Award Approval Popup" && awardApprovalParticipants && (
          <AwardApprovalPopup
            isOpen={isOpen}
            setOpen={setOpen}
            toggle={toggle}
            organization={organization}
            program={program}
            statusName={statusName}
            awardApprovalParticipants={awardApprovalParticipants}
            rejection_notes={`User Rejected by ${auth?.name}`}
            onSubmit={onSubmit}
          />
        )}
      </div>
    );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    organization: state.organization,
    program: state.program,
  };
};
export default connect(mapStateToProps)(ManageScheduleAwardApprovals);
